import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { collection, doc, addDoc, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import CustomerModal from '../components/forms/CustomerModal';
import CustomerMatchDialog from '../components/forms/CustomerMatchDialog';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import PaginationControls from '../components/common/PaginationControls';
import { useNavigate } from 'react-router-dom';
import { useTableState } from '../hooks/useTableState';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import { useUserRole } from '../hooks/useUserRole';
import { exportData, checkExportSize, requestBackgroundExport } from '../utils/exportUtils';
import ExportButton from '../components/common/ExportButton';
import { formatDate } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';

import { mergeCustomers, markCustomerReviewed } from '../utils/customerMergeUtils';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, canViewAllData, getRbacMatchNames } from '../utils/permissions';
import { parseCSVFile, importAndSyncCollectionFromCSV } from '../utils/importUtils';
import { usePaginatedData, monitorQuery } from '../utils/paginationUtils';
import { useFirestoreListener } from '../utils/listenerManager';
import { handleFirestoreError, PerformanceMonitor } from '../utils/errorHandling';
import { search as typesenseSearch, isTypesenseAvailable } from '../utils/typesense';
import { logEntityDeletion } from '../utils/logger';

const PAGE_SIZE = 50; // Show 50 customers at a time (or all loaded in browse mode)
const USERS_PAGE_SIZE = 100;
const SEARCH_THRESHOLD = 3; // Minimum characters to trigger server-side search

const CustomerPage = ({ user }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [toast, setToast] = useState(null);
  const fb = useFilterBar({
    search: '',
    stageFilter: '',
    salesRepFilter: '',
    businessFocusFilter: '',
    currentMode: 'browse',
    searchQuery: '',
  });
  const [focusOptions, setFocusOptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);

  // Server-side pagination state
  const [currentBaseQuery, setCurrentBaseQuery] = useState(null);

  // Selection state
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Sorting state (synced to URL for back-button support)
  const { sortBy: sortField, sortDir: sortDirection, page: currentPage, setSort: setTableSort, setPage: setCurrentPage } = useTableState({ sortBy: 'customerName', sortDir: 'asc', page: 1 });
  const customerInitialLoadRef = useRef(false);

  // Review mode state
  const [showReviewMode, setShowReviewMode] = useState(false);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [customerToMatch, setCustomerToMatch] = useState(null);
  const [merging, setMerging] = useState(false);
  const [dismissedReviewBanner, setDismissedReviewBanner] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Typesense state
  const [typesenseEnabled, setTypesenseEnabled] = useState(false);
  const [typesenseResults, setTypesenseResults] = useState([]);
  const [searchingTypesense, setSearchingTypesense] = useState(false);
  // Set when a Typesense query errors (e.g. bad API key) so we fall back to Firestore
  const [typesenseFailed, setTypesenseFailed] = useState(false);

  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userRole = useUserRole(user);
  const { can, loading: permissionsLoading } = usePermissions(user);

  // Helper to get current user's displayName for filtering (needed by Typesense RBAC)
  // Synchronous useMemo avoids race conditions with async useEffect
  const userDisplayName = useMemo(() => {
    if (!user || permissionsLoading || can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL)) return '';
    return user.displayName || user.full_name || '';
  }, [user, permissionsLoading, can]);



  // Check Typesense availability on mount
  useEffect(() => {
    const checkTypesense = async () => {
      const available = await isTypesenseAvailable();
      setTypesenseEnabled(available);
      // ...existing code...
    };
    checkTypesense();
  }, []);

  // Typesense search effect (instant search while typing)
  useEffect(() => {
    if (!typesenseEnabled || !fb.applied.search.trim()) {
      setTypesenseResults([]);
      setSearchingTypesense(false);
      return;
    }
    // Wait for permissions to load before searching (affects RBAC filter)
    if (permissionsLoading) return;

    const searchTypesense = async () => {
      setSearchingTypesense(true);
      try {
        const startTime = performance.now();
        
        // Build filter query for Typesense (server-side filtering)
        const filterBy = [];
        
        // RBAC: Non-admin users can only see customers they're assigned to
        if (!can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) && userDisplayName) {
          // Use both displayName and full_name for matching
          const rbacNames = [];
          if (user?.displayName) rbacNames.push(user.displayName.trim());
          if (user?.full_name && user.full_name.trim().toLowerCase() !== (user?.displayName || '').trim().toLowerCase()) {
            rbacNames.push(user.full_name.trim());
          }
          if (rbacNames.length > 0) {
            filterBy.push(`salesRepNames:=[${rbacNames.join(',')}]`);
          }
        }
        
        if (fb.applied.stageFilter) {
          filterBy.push(`customerStatus:=${fb.applied.stageFilter}`);
        }
        if (fb.applied.salesRepFilter && fb.applied.salesRepFilter !== '(No Sales Rep)') {
          filterBy.push(`salesRepNames:=${fb.applied.salesRepFilter}`);
        }
        
        const searchParams = {
          q: fb.applied.search,
          query_by: 'customerName,customerEmail,apparelMagicId,city,state',
          per_page: 250, // Increased from 100 for better coverage
          sort_by: 'customerName:asc'
        };
        
        // Add server-side filters if present
        if (filterBy.length > 0) {
          searchParams.filter_by = filterBy.join(' && ');
        }
        
        const response = await typesenseSearch('customers', searchParams);

        const results = response.hits.map(hit => ({
          id: hit.document.id,
          customerName: hit.document.customerName || '',
          customerEmail: hit.document.customerEmail || '',
          customerPhone: hit.document.customerPhone || '',
          customerCity: hit.document.city || '',
          customerState: hit.document.state || '',
          customerAccountNumber: hit.document.apparelMagicId || '',
          apparelMagicId: hit.document.apparelMagicId || '',
          customerStatus: hit.document.customerStatus || '',
          businessFocus: hit.document.businessFocus || [],
          salesRepNames: hit.document.salesRepNames || [],
          needsReview: hit.document.needsReview || false,
          // Map salesRepNames to salesReps array format expected by UI
          salesReps: (hit.document.salesRepNames || []).map(name => ({
            salesRepName: name,
            displayName: name
          }))
        }));

        // ...existing code...
        setTypesenseResults(results);
        setTypesenseFailed(false);
      } catch (error) {
        console.error('[CustomerPage] Typesense search error, falling back to Firestore:', error);
        setTypesenseResults([]);
        setTypesenseFailed(true);
      } finally {
        setSearchingTypesense(false);
      }
    };

    searchTypesense();
  }, [fb.applied.search, typesenseEnabled, fb.applied.stageFilter, fb.applied.salesRepFilter, can, userDisplayName, permissionsLoading]);

  // Pagination and filtering state
  const [totalPages, setTotalPages] = useState(1);
  const [filteredMode, setFilteredMode] = useState(false);
  const [filteredList, setFilteredList] = useState([]);
  
  // Lazy loading state
  const [hasMoreCustomers, sethasMoreCustomers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const lastVisibleRef = useRef(null); // Use ref to avoid stale closures
  
  // Computed values
  const searchLower = fb.applied.search.toLowerCase().trim();
  
  // Constants for pagination
  const INITIAL_LOAD = 50; // Hybrid: Load first 50 customers
  const LOAD_MORE_BATCH = 50; // Load 50 more at a time
  const ADMIN_MAX_FETCH = 10000;
  const MAX_FILTER_FETCH = 2000;

  // userDisplayName is now a useMemo (above) — no async effect needed

  // Create base query based on current filters
  const createBaseQuery = useCallback(() => {
    let baseQuery = collection(db, collectionPath('customers'));
    const filters = [];
    
    // Add status filter if selected
    if (fb.applied.stageFilter) {
      filters.push({ field: 'customerStatus', operator: '==', value: fb.applied.stageFilter });
    }
    
    // Add review filter
    if (showReviewMode) {
      filters.push({ field: 'needsReview', operator: '==', value: true });
    }
    
    // Apply filters to query
    filters.forEach(filter => {
      baseQuery = query(baseQuery, where(filter.field, filter.operator, filter.value));
    });
    
    // Add ordering
    baseQuery = query(baseQuery, orderBy(sortField, sortDirection));
    
    return baseQuery;
  }, [fb.applied.stageFilter, showReviewMode, sortField, sortDirection]);
  
  // Update base query when filters change
  useEffect(() => {
    const newQuery = createBaseQuery();
    setCurrentBaseQuery(newQuery);
  }, [createBaseQuery]);

  // Check for customers needing review with a lightweight query (no need to load all 2000 customers)
  const [reviewCount, setReviewCount] = useState(0);
  const [customersNeedingReview, setCustomersNeedingReview] = useState([]);
  
  // Separate query just to count customers needing review (runs once on mount)
  useEffect(() => {
    if (permissionsLoading) return; // wait until permissions are resolved
    const checkForReviews = async () => {
      try {
        const isViewAll = can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL);
        let reviewQuery = query(
          collection(db, collectionPath('customers')),
          where('needsReview', '==', true),
          where('customerStatus', '==', 'Lead')
        );
        // For non-admins, also filter to only their assigned customers
        if (!isViewAll && userDisplayName) {
          reviewQuery = query(reviewQuery, where('salesRepNames', 'array-contains', userDisplayName));
        }
        const snapshot = await getDocs(reviewQuery);
        const count = snapshot.size;
        setReviewCount(count);
        
        // Only load the actual customer data if in review mode
        if (showReviewMode) {
          const reviewCustomers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setCustomersNeedingReview(reviewCustomers);
        }
        
        // ...existing code...
      } catch (error) {
        console.error('[CustomerPage] Error checking for reviews:', error);
      }
    };
    
    checkForReviews();
  }, [showReviewMode, permissionsLoading, can, userDisplayName]); // Re-run when entering/exiting review mode

  // Client-side filter function - applies all filters
  function applyClientFilter(list) {
    let filtered = [...list];
    
    // Filter by review mode
    if (showReviewMode) {
      filtered = filtered.filter(c => c.needsReview === true);
    }
    
    // Filter by status (but don't filter out Lead status when checking for review banner)
    if (fb.applied.stageFilter && !showReviewMode) {
      filtered = filtered.filter(c => c.customerStatus === fb.applied.stageFilter);
    }
    
    // Filter by customer name, email, and customer ID
    if (searchLower) {
      filtered = filtered.filter(c => {
        // Search customer ID
        if ((c.id || '').toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search customer name
        if ((c.customerName || '').toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search customer email
        if ((c.customerEmail || '').toLowerCase().includes(searchLower)) {
          return true;
        }
        // Search contact emails for this customer
        // Check both: 1) contacts collection, 2) embedded customer.contacts array (ApparelMagic)
        const collectionContacts = contactsBycustomerId.get(c.id) || [];
        const embeddedContacts = c.contacts || [];
        const allCustomerContacts = [...collectionContacts, ...embeddedContacts];
        // Check both 'email' and 'contactEmail' fields (for both types of contacts)
        const hasMatchingContactEmail = allCustomerContacts.some(contact => {
          const contactEmail = (contact.email || contact.contactEmail || '').toLowerCase();
          return contactEmail.includes(searchLower);
        });
        return hasMatchingContactEmail;
      });
    }
    
    // Filter by status
    if (fb.applied.stageFilter) {
      filtered = filtered.filter(c => 
        c.customerStatus === fb.applied.stageFilter
      );
    }
    
    // Filter by sales rep
    if (fb.applied.salesRepFilter) {
      if (fb.applied.salesRepFilter === '(No Sales Rep)') {
        // Show customers with no sales reps or empty salesReps array
        filtered = filtered.filter(c => 
          !c.salesReps || 
          c.salesReps.length === 0 ||
          c.salesReps.every(rep => !rep.salesRepName)
        );
      } else {
        // Show customers that have this specific sales rep
        filtered = filtered.filter(c => 
          c.salesReps && c.salesReps.some(rep => 
            rep.salesRepName === fb.applied.salesRepFilter
          )
        );
      }
    }
    
    // Filter by business focus
    if (fb.applied.businessFocusFilter) {
      filtered = filtered.filter(c => {
        // Sentinel value for matching customers with no business focus set
        if (fb.applied.businessFocusFilter === '__EMPTY__') {
          if (c.businessFocus === undefined || c.businessFocus === null || c.businessFocus === '') return true;
          if (Array.isArray(c.businessFocus) && c.businessFocus.length === 0) return true;
          return false;
        }
        if (!c.businessFocus) return false;
        // businessFocus can be array or string
        if (Array.isArray(c.businessFocus)) {
          return c.businessFocus.includes(fb.applied.businessFocusFilter);
        }
        return c.businessFocus === fb.applied.businessFocusFilter;
      });
    }
    
    return filtered;
  }

  // BEST PRACTICE: Fetch users and contacts in parallel to minimize latency
  useEffect(() => {
    if (!user) return;
    
    // Parallel fetch - no data dependency
    Promise.all([
      getDocs(query(
        collection(db, collectionPath('users')), 
        orderBy('firstName', 'asc'), 
        limit(USERS_PAGE_SIZE)
      )),
      getDocs(query(
        collection(db, collectionPath('contacts')),
        limit(5000) // BEST PRACTICE: Add limit to prevent hotspotting
      )),
      getDocs(collection(db, collectionPath('focusOptions')))
    ]).then(([usersSnap, contactsSnap, focusSnap]) => {
      setUsers(usersSnap.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
      setContacts(contactsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      // Use doc IDs as option values; seed with the same defaults CustomerModal uses
      const DEFAULT_FOCUS = ['Golf', 'Tennis', 'Pickleball', 'Other'];
      const fromFirestore = focusSnap.docs.map(doc => doc.id).filter(Boolean);
      const merged = [...new Set([...DEFAULT_FOCUS, ...fromFirestore])].sort();
      setFocusOptions(merged);
    }).catch(err => {
      console.error('Error fetching users/contacts/focusOptions:', err);
    });
  }, [user]);

  // Create Map for O(1) user lookups
  const usersById = useMemo(() => {
    const map = new Map();
    users.forEach(u => map.set(u.uid, u));
    return map;
  }, [users]);

  // Contacts lookup by customer ID
  const contactsBycustomerId = useMemo(() => {
    const map = new Map();
    contacts.forEach(contact => {
      const customerId = contact.customerId;
      if (customerId) {
        if (!map.has(customerId)) {
          map.set(customerId, []);
        }
        map.get(customerId).push(contact);
      }
    });
    return map;
  }, [contacts]);

  // Get unique sales rep names from the users list (not from loaded customers)
  const availableSalesReps = useMemo(() => {
    return users
      .map(u => u.displayName || u.full_name || '')
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [users]);

  // Fetch initial batch of customers (50) with lazy loading support
  const fetchCustomers = useCallback(async (loadMore = false) => {
    const perfId = PerformanceMonitor.startOperation(loadMore ? 'load-more-customers' : 'fetch-customers');
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      let base = collection(db, collectionPath('customers'));
      
      const needsRbac = !can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) && userDisplayName;
      const targetCount = loadMore ? LOAD_MORE_BATCH : INITIAL_LOAD;
      const matchSet = needsRbac ? new Set(getRbacMatchNames(user)) : null;
      
      // For RBAC users, fetch larger raw batches to reduce round trips
      const RAW_BATCH = 200;
      const batchSize = needsRbac ? RAW_BATCH : targetCount;
      let accumulated = [];
      let cursor = loadMore ? lastVisibleRef.current : null;
      let exhausted = false;
      
      if (loadMore && !cursor) {
        console.warn('[CustomerPage] ⚠️ Cannot load more - lastVisible is null');
        setLoadingMore(false);
        return;
      }
      
      while (accumulated.length < targetCount && !exhausted) {
        let qArr = [orderBy('customerName', 'asc')];
        
        if (cursor) {
          qArr.push(startAfter(cursor));
        }
        qArr.push(limit(batchSize));
        
        const customersQuery = query(base, ...qArr);
        const snap = await monitorQuery(loadMore ? 'load-more-customers' : 'fetch-customers', () => getDocs(customersQuery));
        
        let rows = snap.docs.map(doc => {
          const data = doc.data();
          const salesRepNames = Array.isArray(data.salesReps)
            ? data.salesReps.map(rep => rep.salesRepName).filter(Boolean)
            : [];
          
          return {
            id: doc.id,
            ...data,
            customerName: data.customerName || '',
            customerStatus: data.customerStatus || '',
            salesRepNames
          };
        });
        
        if (needsRbac) {
          rows = rows.filter(c => {
            if (!Array.isArray(c.salesReps)) return false;
            return c.salesReps.some(rep => 
              rep.salesRepName && matchSet.has(rep.salesRepName.toLowerCase().trim())
            );
          });
        }
        
        accumulated.push(...rows);
        
        // Update cursor for next iteration
        if (snap.docs.length > 0) {
          cursor = snap.docs[snap.docs.length - 1];
        }
        
        // Stop if Firestore returned fewer docs than requested (no more data)
        if (snap.docs.length < batchSize) {
          exhausted = true;
        }
        
        // Admins don't need RBAC loop
        if (!needsRbac) break;
      }
      
      console.log(`[CustomerPage] 🔒 Accumulated ${accumulated.length} customers (target: ${targetCount}, exhausted: ${exhausted})`);

      // Update state: append if loading more, replace if initial load
      if (loadMore) {
        setCustomers(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newRows = accumulated.filter(c => !existingIds.has(c.id));
          const combined = [...prev, ...newRows];
          setTotalPages(Math.max(1, Math.ceil(combined.length / PAGE_SIZE)));
          return combined;
        });
      } else {
        setCustomers(accumulated);
        setTotalPages(Math.max(1, Math.ceil(accumulated.length / PAGE_SIZE)));
      }
      
      // Track cursor for next load
      if (cursor) {
        lastVisibleRef.current = cursor;
      }
      
      sethasMoreCustomers(!exhausted);
      
      PerformanceMonitor.endOperation(perfId, true);
    } catch (err) {
      PerformanceMonitor.endOperation(perfId, false);
      console.error('fetchCustomers error:', err);
      const friendlyMsg = handleFirestoreError(err, 'fetch-customers');
      setCustomers([]);
      setError(friendlyMsg);
      setToast({ message: friendlyMsg, type: 'error' });
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [can, userDisplayName]);

  // Load more customers (lazy loading)
  const loadMoreCustomers = async () => {
    if (!hasMoreCustomers || loadingMore) return;
    await fetchCustomers(true); // Pass loadMore = true
  };

  // NEW: cache of the raw fetched list when in filtered mode so we can re-filter instantly on search changes without requery
  const [filterBase, setFilterBase] = useState([]); // holds list before client search slice

  // Load filtered dataset (status and/or search) — single Firestore fetch per change of status OR entering filtered mode
  const loadFilteredBase = useCallback(async () => {
    const perfId = PerformanceMonitor.startOperation('load-filtered-customers');
    setLoading(true);
    setError('');
    try {
      // Fetch all customers (no Firestore filters except user-based)
      // All filtering (status, sales rep, name search) will be done client-side
      
      // Determine fetch limit based on user permissions
      const fetchLimit = can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) ? ADMIN_MAX_FETCH : MAX_FILTER_FETCH;

      // Firestore query (minimal filters)
      const baseQ = query(
        collection(db, collectionPath('customers')),
        orderBy('customerName', 'asc'),
        limit(fetchLimit)
      );
      const snap = await getDocs(baseQ);
      let baseList = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          customerName: data.customerName || '',
          customerStatus: data.customerStatus || ''
        };
      });

      // Filter customers for non-admin users by matching displayName/full_name with salesRepName
      if (!canViewAllData(currentUser) && !can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) && userDisplayName) {
        const matchNames = getRbacMatchNames(currentUser);
        const matchSet = new Set(matchNames);
        baseList = baseList.filter(c => {
          if (!Array.isArray(c.salesReps)) return false;
          return c.salesReps.some(rep => 
            rep.salesRepName && matchSet.has(rep.salesRepName.toLowerCase().trim())
          );
        });
      }
      
      setFilterBase(baseList);
      
      // Warn if we hit the fetch limit
      if (baseList.length === fetchLimit) {
        console.warn(`Fetched maximum ${fetchLimit} customers. Some results may be missing from search.`);
        setToast({
          message: `Loaded ${fetchLimit} customers. If you don't find what you're looking for, try a more specific search.`,
          type: 'warning'
        });
      }
      
      // Apply all client-side filters (search, status, sales rep)
      const filtered = applyClientFilter(baseList);
      setFilteredList(filtered);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)));
      setFilteredMode(true);
      PerformanceMonitor.endOperation(perfId, true);
    } catch (err) {
      PerformanceMonitor.endOperation(perfId, false);
      console.error('loadFilteredBase error:', err);
      const friendlyMsg = handleFirestoreError(err, 'load-filtered-customers');
      setFilterBase([]);
      setFilteredList([]);
      setFilteredMode(true);
      setError(friendlyMsg);
      setToast({ message: friendlyMsg, type: 'error' });
    }
    setLoading(false);
  }, [userRole, userDisplayName, applyClientFilter, can]);

  // Re-apply client filters when any filter changes (without refetching Firestore)
  useEffect(() => {
    if (!filteredMode) return; // only when already in filtered mode
    if (!fb.applied.stageFilter && !searchLower && !fb.applied.salesRepFilter && !fb.applied.businessFocusFilter && !showReviewMode) return; // nothing to filter
    // Re-filter from filterBase
    const filtered = applyClientFilter(filterBase);
    setFilteredList(filtered);
    setTotalPages(Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)));
    if (customerInitialLoadRef.current) {
      setCurrentPage(1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchLower, fb.applied.stageFilter, fb.applied.salesRepFilter, fb.applied.businessFocusFilter, showReviewMode]);

  // Decide mode when any filter is active
  useEffect(() => {
    if (!user || !userRole || permissionsLoading) return;
    // For non-admins, wait for userDisplayName to be loaded
    if (!can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) && !userDisplayName) return;
    
    const wantsFiltered = !!fb.applied.stageFilter || !!searchLower || !!fb.applied.salesRepFilter || !!fb.applied.businessFocusFilter || showReviewMode;
    
    if (wantsFiltered) {
      if (customerInitialLoadRef.current) {
        setCurrentPage(1);
      }
      setFilteredMode(true);
      
      // Only load from Firestore if we have filters but NO search (Firestore needed for status/sales rep/focus only)
      // When search is active: Typesense handles it if available, otherwise fall back to Firestore
      const needsFirestore = !searchLower && (fb.applied.stageFilter || fb.applied.salesRepFilter || fb.applied.businessFocusFilter || showReviewMode);
      const needsFirestoreForSearch = searchLower && (!typesenseEnabled || typesenseFailed);
      
      if (needsFirestore || needsFirestoreForSearch) {
        // ...existing code...
        loadFilteredBase();
      } else {
        // ...existing code...
      }
    } else {
      setFilteredMode(false);
      setFilterBase([]);
      setFilteredList([]);
      setTotalPages(1);
      if (customerInitialLoadRef.current) {
        setCurrentPage(1);
      }
      customerInitialLoadRef.current = true;
      fetchCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fb.applied.stageFilter, searchLower, fb.applied.salesRepFilter, fb.applied.businessFocusFilter, showReviewMode, user, userRole, userDisplayName, permissionsLoading, typesenseEnabled, typesenseFailed]);

  // Pagination handlers for in-memory pagination
  // Note: These reference calculatedTotalPages which is computed later
  const handleNextPage = () => {
    // Use sortedCustomers.length to calculate max pages dynamically
    const maxPages = Math.max(1, Math.ceil(sortedCustomers.length / PAGE_SIZE));
    if (currentPage < maxPages) {
      setCurrentPage(p => p + 1);
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(p => p - 1);
    }
  };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Current page slice (table renders this)
  // Priority: Typesense results (when searching) > Filtered list > All customers
  const allCustomers = useMemo(() => {
    // If Typesense is enabled and user is searching, use Typesense results
    if (typesenseEnabled && !typesenseFailed && fb.applied.search.trim() && typesenseResults.length >= 0) {
      // Only apply client-side filters that aren't handled by Typesense server-side
      let filtered = [...typesenseResults];
      
      // Review mode (not in Typesense filter)
      if (showReviewMode) {
        filtered = filtered.filter(c => c.needsReview === true);
      }
      
      // "(No Sales Rep)" special case (not in Typesense filter)
      if (fb.applied.salesRepFilter === '(No Sales Rep)') {
        filtered = filtered.filter(c => 
          !c.salesReps || 
          c.salesReps.length === 0 ||
          c.salesReps.every(rep => !rep.salesRepName)
        );
      }
      
      // Note: stageFilter and regular salesRepFilter are handled server-side by Typesense
      
      if (filtered.length !== typesenseResults.length) {
        // ...existing code...
      }
      return filtered;
    }
    // Otherwise use existing Firestore logic
    return filteredMode ? filteredList : customers;
  }, [typesenseEnabled, typesenseFailed, fb.applied.search, typesenseResults, filteredMode, filteredList, customers, showReviewMode, fb.applied.stageFilter, fb.applied.salesRepFilter]);
  
  // Apply sorting to customers before pagination
  const sortedCustomers = useMemo(() => {
    if (!allCustomers.length) return [];
    
    const sorted = [...allCustomers].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'customerId':
          aVal = (a.customerAccountNumber || a.apparelMagicId || a.id || '').toLowerCase();
          bVal = (b.customerAccountNumber || b.apparelMagicId || b.id || '').toLowerCase();
          break;
        case 'customerName':
          aVal = (a.customerName || '').toLowerCase();
          bVal = (b.customerName || '').toLowerCase();
          break;
        case 'customerEmail':
          aVal = (a.customerEmail || '').toLowerCase();
          bVal = (b.customerEmail || '').toLowerCase();
          break;
        case 'customerCity':
          aVal = (a.customerCity || '').toLowerCase();
          bVal = (b.customerCity || '').toLowerCase();
          break;
        case 'customerState':
          aVal = (a.customerState || '').toLowerCase();
          bVal = (b.customerState || '').toLowerCase();
          break;
        case 'salesRep':
          // Get first sales rep name
          aVal = (a.salesReps && a.salesReps.length > 0 ? a.salesReps[0].salesRepName : '').toLowerCase();
          bVal = (b.salesReps && b.salesReps.length > 0 ? b.salesReps[0].salesRepName : '').toLowerCase();
          break;
        case 'status':
          aVal = (a.customerStatus || '').toLowerCase();
          bVal = (b.customerStatus || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [allCustomers, sortField, sortDirection]);
  
  // In browse mode (no search/filter), show ALL loaded customers (infinite scroll)
  // In search/filter mode, use pagination to handle large result sets
  const isSearchMode = !!fb.applied.search.trim();
  const isFilteredMode = fb.applied.stageFilter || fb.applied.salesRepFilter || fb.applied.businessFocusFilter || showReviewMode;
  const usePagination = isSearchMode || isFilteredMode;
  
  // Calculate total pages based on current data (important for Typesense results)
  const calculatedTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(sortedCustomers.length / PAGE_SIZE));
  }, [sortedCustomers.length]);
  
  const visibleCustomers = usePagination
    ? sortedCustomers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    : sortedCustomers; // Show all in browse mode

  // Sorting handler
  const handleSort = (field) => {
    const newDir = sortField === field ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    setTableSort(field, newDir);
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-400">⇅</span>;
    }
    return (
      <span className="ml-1 text-indigo-600">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  // Selection handlers - memoized to prevent re-renders
  const handleSelectCustomer = useCallback((customerId, checked) => {
    setSelectedCustomers(prev => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(customerId);
      } else {
        newSelected.delete(customerId);
      }
      // Update selectAll state based on visible customers
      const allVisible = visibleCustomers.map(c => c.id);
      setSelectAll(newSelected.size === allVisible.length && allVisible.length > 0 && 
                   allVisible.every(id => newSelected.has(id)));
      return newSelected;
    });
  }, [visibleCustomers]);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      const allIds = new Set(visibleCustomers.map(c => c.id));
      setSelectedCustomers(allIds);
    } else {
      setSelectedCustomers(new Set());
    }
    setSelectAll(checked);
  }, [visibleCustomers]);

  // Helper function to get sales rep display name - memoized for performance
  const getSalesRepDisplay = useCallback((cust) => {
    if (Array.isArray(cust.salesReps) && cust.salesReps.length > 0) {
      return cust.salesReps.map(rep =>
        rep.salesRepName ||
        [rep.firstName, rep.lastName].filter(Boolean).join(' ') ||
        rep.name ||
        rep.displayName ||
        rep.email ||
        '-'
      ).join(', ');
    }
    
    // Fallback to usersById Map for O(1) lookup
    const user = usersById.get(cust.uid);
    return user
      ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.displayName || '-'
      : '-';
  }, [usersById]);

  // Update select all checkbox when visible customers change
  useEffect(() => {
    const visibleIds = visibleCustomers.map(c => c.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedCustomers.has(id));
    setSelectAll(allVisibleSelected);
  }, [visibleCustomers, selectedCustomers]);

  // Fetch all customers from Firestore for export
  const fetchallCustomersForExport = async () => {
    try {
      const cons = [];
      // For non-admins, we'll filter client-side after fetching
      
      const allQuery = query(
        collection(db, collectionPath('customers')),
        ...cons,
        orderBy('customerName', 'asc')
      );
      const snap = await getDocs(allQuery);
      let rows = snap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          customerName: data.customerName || '',
          customerStatus: data.customerStatus || ''
        };
      });

      // Filter customers for non-admin users by matching displayName/full_name with salesRepName
      if (!canViewAllData(currentUser) && !can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) && userDisplayName) {
        const matchNames = getRbacMatchNames(currentUser);
        const matchSet = new Set(matchNames);
        rows = rows.filter(c => {
          if (!Array.isArray(c.salesReps)) return false;
          return c.salesReps.some(rep => 
            rep.salesRepName && matchSet.has(rep.salesRepName.toLowerCase().trim())
          );
        });
      }

      return rows;
    } catch (err) {
      console.error('Failed to fetch all customers:', err);
      throw new Error('Failed to fetch all customers for export');
    }
  };

  // Export with enhanced details (locations, contacts, business info)
  const handleExport = async (format) => {
    try {
      let exportSource;
      let baseFilename;
      
      if (selectedCustomers.size > 0) {
        // Export selected customers
        const currentSource = filteredMode ? filteredList : customers;
        exportSource = currentSource.filter(c => selectedCustomers.has(c.id));
        baseFilename = `customers-selected-${selectedCustomers.size}`;
      } else {
        // Export all customers from Firestore
        setLoading(true);
        exportSource = await fetchallCustomersForExport();
        baseFilename = 'customers-all';
        setLoading(false);
      }

      // Check export size limits
      const sizeCheck = checkExportSize(format, exportSource.length);
      
      if (!sizeCheck.allowed) {
        setLoading(false);
        alert(sizeCheck.message);
        return;
      }
      
      // Handle background export for large datasets
      if (sizeCheck.level === 'background') {
        const confirmed = window.confirm(
          `${sizeCheck.message}\n\n` +
          `This export will be processed in the background. ` +
          `You'll receive an email with a download link when it's ready (typically 5-10 minutes).\n\n` +
          `Continue with background export?`
        );
        
        if (!confirmed) {
          setLoading(false);
          return;
        }
        
        try {
          setLoading(true);
          
          // Request background export
          const jobId = await requestBackgroundExport({
            exportType: 'customers',
            format,
            recordCount: exportSource.length,
            filters: {
              selectedCustomers: selectedCustomers.size > 0 ? Array.from(selectedCustomers) : null,
              stageFilter: fb.applied.stageFilter,
              salesRepFilter: fb.applied.salesRepFilter,
              businessFocusFilter: fb.applied.businessFocusFilter,
              searchQuery: fb.applied.searchQuery
            },
            userId: currentUser.uid,
            userEmail: currentUser.email,
            fileName: baseFilename
          });
          
          setLoading(false);
          setToast({
            type: 'success',
            message: `Background export started! Check your email (${currentUser.email}) in 5-10 minutes for the download link.`
          });
          
          console.log('Background export job created:', jobId);
          return;
        } catch (err) {
          console.error('Failed to request background export:', err);
          setToast({
            type: 'error',
            message: `Failed to start background export: ${err.message}`
          });
          setLoading(false);
          return;
        }
      }
      
      if (sizeCheck.level === 'warning') {
        const confirmed = window.confirm(sizeCheck.message);
        if (!confirmed) {
          setLoading(false);
          return;
        }
      }

      // Different headers for PDF vs CSV/Excel (PDF needs fewer, simpler columns)
      const isPDF = format.toLowerCase() === 'pdf';
      
      const headers = isPDF ? [
        "ID", "Customer Name", "Email", "Phone", "Status", 
        "City", "State", "Business Type", "Sales Reps"
      ] : [
        "Customer ID", "Customer Name", "Email", "Phone", "Status", 
        "Address 1", "Address 2", "City", "State", "Zip", "Country", 
        "Website", "Business Type", "Business Focus", "Sales Reps",
        "Locations", "Contacts"
      ];
      
      const rowMapper = isPDF
        ? (c => [
            c.customerAccountNumber || c.id || '',
            c.customerName || '',
            c.customerEmail || '',
            c.customerPhone || '',
            c.customerStatus || '',
            c.customerCity || '',
            c.customerState || '',
            c.businessType || '',
            Array.isArray(c.salesReps) && c.salesReps.length > 0
              ? c.salesReps.map(rep =>
                rep.salesRepName || rep.name || rep.displayName || rep.email || '-'
              ).join(', ')
              : users.find(u => u.uid === c.uid)?.firstName || users.find(u => u.uid === c.uid)?.displayName || '-'
          ])
        : (c => {
            // Format locations as JSON string
            const locationsStr = Array.isArray(c.locations) && c.locations.length > 0
              ? JSON.stringify(c.locations.map(loc => ({
                  name: loc.locationName || '',
                  email: loc.locationEmail || '',
                  phone: loc.locationPhone || '',
                  storeNumber: loc.locationStoreNumber || '',
                  isMain: loc.locationIsMainLocation || '0',
                  address1: loc.locationAddress1 || '',
                  address2: loc.locationAddress2 || '',
                  city: loc.locationCity || '',
                  state: loc.locationState || '',
                  zip: loc.locationZip || '',
                  country: loc.locationCountry || ''
                })))
              : '';
            
            // Format contacts as JSON string
            const contactsStr = Array.isArray(c.contacts) && c.contacts.length > 0
              ? JSON.stringify(c.contacts.map(contact => ({
                  firstName: contact.contactFirstName || contact.firstName || '',
                  lastName: contact.contactLastName || contact.lastName || '',
                  email: contact.contactEmail || contact.email || '',
                  phone: contact.contactPhone || contact.phone || '',
                  title: contact.contactPosition || contact.title || '',
                  isPrimary: contact.contactIsMainContact || contact.isPrimary || '0',
                  businessType: contact.businessType || '',
                  focus: Array.isArray(contact.contactFocus) ? contact.contactFocus.join(';') : 
                         Array.isArray(contact.focus) ? contact.focus.join(';') : ''
                })))
              : '';
            
            return [
              c.customerAccountNumber || c.id || '',
              c.customerName || '',
              c.customerEmail || '',
              c.customerPhone || '',
              c.customerStatus || '',
              c.customerAddress1 || '',
              c.customerAddress2 || '',
              c.customerCity || '',
              c.customerState || '',
              c.customerZip || '',
              c.customerCountry || '',
              c.customerWebsite || '',
              c.businessType || '',
              Array.isArray(c.businessFocus) ? c.businessFocus.join(';') : '',
              Array.isArray(c.salesReps) && c.salesReps.length > 0
                ? c.salesReps.map(rep =>
                  rep.salesRepName || rep.name || rep.displayName || rep.email || '-'
                ).join(', ')
                : users.find(u => u.uid === c.uid)?.firstName || users.find(u => u.uid === c.uid)?.displayName || '-',
              locationsStr,
              contactsStr
            ];
          });
      
      // Export using unified export function
      exportData({
        filename: baseFilename,
        format,
        headers,
        data: exportSource,
        rowMapper,
        options: isPDF ? {
          title: `Customer Export - ${new Date().toLocaleDateString()}`,
          fontSize: 8,
          orientation: 'landscape',
          pageSize: 'a4'
        } : {
          title: `Customer Export - ${new Date().toLocaleDateString()}`,
          sheetName: 'Customers',
          columnWidths: [12, 20, 20, 15, 10, 20, 20, 15, 10, 10, 12, 20, 15, 20, 25, 30, 30]
        }
      });
      
      setToast({ 
        type: 'success', 
        message: `Exported ${exportSource.length} customers to ${format.toUpperCase()}` 
      });
    } catch (err) {
      console.error('Export failed:', err);
      setToast({
        message: 'Failed to export customers: ' + err.message,
        type: 'error'
      });
      setLoading(false);
    }
  };

  // Delete Customer
  const handleDeleteCustomer = async (cust) => {
    // Check permissions
    if (!can(FEATURE_PERMISSIONS.CUSTOMER_DELETE)) {
      setToast({
        message: 'You do not have permission to delete customers',
        type: 'error'
      });
      return;
    }

    // Confirm deletion
    const confirmed = window.confirm(
      `⚠️ Delete Customer ⚠️\n\n` +
      `Are you sure you want to delete "${cust.customerName}"?\n\n` +
      `This will permanently remove the customer record.\n` +
      `Related opportunities, orders, and contacts will remain but will be orphaned.`
    );

    if (!confirmed) return;

    try {
      // Import deleteDoc here if not already imported
      const { deleteDoc } = await import('firebase/firestore');
      
      // Delete the customer
      await deleteDoc(doc(db, collectionPath('customers'), cust.id));
      
      // Log the deletion to security logs
      logEntityDeletion({
        user: currentUser,
        entityType: 'Customer',
        entityId: cust.id,
        entityName: cust.customerName
      }).catch(e => console.warn('Failed to log deletion:', e));

      setToast({
        message: `Customer "${cust.customerName}" deleted successfully`,
        type: 'success'
      });

      // Refresh the list
      if (filteredMode) {
        await loadFilteredBase();
      } else {
        await fetchCustomers();
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setToast({
        message: `Failed to Delete Customer: ${err.message}`,
        type: 'error'
      });
    }
  };

  // Bulk delete selected customers
  const handleBulkDelete = async () => {
    // Check permissions
    if (!can(FEATURE_PERMISSIONS.CUSTOMER_DELETE)) {
      setToast({
        message: 'You do not have permission to delete customers',
        type: 'error'
      });
      return;
    }

    if (selectedCustomers.size === 0) {
      setToast({
        message: 'No customers selected',
        type: 'error'
      });
      return;
    }

    // Get selected customer names for confirmation dialog
    const currentSource = filteredMode ? filteredList : customers;
    const selectedList = currentSource.filter(c => selectedCustomers.has(c.id));
    const customerNames = selectedList.map(c => c.customerName).join(', ');
    const truncatedNames = customerNames.length > 200 
      ? customerNames.substring(0, 200) + '...' 
      : customerNames;

    // Confirm bulk deletion
    const confirmed = window.confirm(
      `⚠️ BULK DELETE WARNING ⚠️\n\n` +
      `Are you sure you want to delete ${selectedCustomers.size} customers?\n\n` +
      `customers to delete:\n${truncatedNames}\n\n` +
      `This will permanently remove these customer records.\n` +
      `Related opportunities, orders, and contacts will remain but will be orphaned.\n\n` +
      `This action cannot be undone!`
    );

    if (!confirmed) return;

    try {
      // Import deleteDoc
      const { deleteDoc } = await import('firebase/firestore');
      
      // Delete all selected customers
      let successCount = 0;
      let errorCount = 0;
      
      // Get customer data for logging before deleting
      const currentSource = filteredMode ? filteredList : customers;
      const selectedData = new Map(
        currentSource.filter(c => selectedCustomers.has(c.id)).map(c => [c.id, c])
      );
      
      for (const customerId of selectedCustomers) {
        try {
          await deleteDoc(doc(db, collectionPath('customers'), customerId));
          // Log the deletion
          const custData = selectedData.get(customerId);
          logEntityDeletion({
            user,
            entityType: 'Customer',
            entityId: customerId,
            entityName: custData?.customerName || customerId,
          }).catch(() => {});
          successCount++;
        } catch (err) {
          console.error(`Failed to Delete Customer ${customerId}:`, err);
          errorCount++;
        }
      }

      // Clear selection
      setSelectedCustomers(new Set());
      setSelectAll(false);

      // Show results
      if (errorCount === 0) {
        setToast({
          message: `Successfully deleted ${successCount} customers`,
          type: 'success'
        });
      } else {
        setToast({
          message: `Deleted ${successCount} customers. ${errorCount} failed.`,
          type: 'error'
        });
      }

      // Refresh the list
      if (filteredMode) {
        await loadFilteredBase();
      } else {
        await fetchCustomers();
      }
    } catch (err) {
      console.error('Bulk delete failed:', err);
      setToast({
        message: `Bulk delete failed: ${err.message}`,
        type: 'error'
      });
    }
  };

  // Destructive Import - replaces entire customers collection with CSV data
  const handleDestructiveImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check permissions
    if (!can(FEATURE_PERMISSIONS.CUSTOMER_IMPORT)) {
      setToast({
        message: 'You do not have permission to import customers',
        type: 'error'
      });
      return;
    }

    // Triple confirmation for destructive operation
    const confirmed1 = window.confirm(
      `⚠️ DESTRUCTIVE IMPORT WARNING ⚠️\n\n` +
      `This will DELETE all customers not in the CSV file!\n\n` +
      `Are you absolutely sure you want to continue?`
    );
    if (!confirmed1) return;

    const confirmed2 = window.confirm(
      `⚠️ SECOND CONFIRMATION ⚠️\n\n` +
      `This action is IRREVERSIBLE!\n\n` +
      `customers not in the CSV will be PERMANENTLY DELETED.\n\n` +
      `Click OK to proceed with destructive import.`
    );
    if (!confirmed2) return;

    const confirmed3 = window.confirm(
      `⚠️ FINAL CONFIRMATION ⚠️\n\n` +
      `Type "DELETE" in the next prompt to confirm.\n\n` +
      `Click OK to continue.`
    );
    if (!confirmed3) return;

    const typedConfirm = prompt('Type DELETE in capital letters to confirm:');
    if (typedConfirm !== 'DELETE') {
      setToast({
        message: 'Import cancelled - confirmation text did not match',
        type: 'error'
      });
      return;
    }

    setImporting(true);
    try {
      // Parse CSV
      const csvRecords = await parseCSVFile(file);
      
      if (!csvRecords || csvRecords.length === 0) {
        throw new Error('No valid records found in CSV file');
      }

      // Transform CSV records to match Firestore schema
      const processedData = csvRecords.map(record => {
        // Parse locations JSON if present
        let locations = [];
        if (record.Locations) {
          try {
            locations = JSON.parse(record.Locations);
          } catch (e) {
            console.warn('Failed to parse locations JSON:', e);
          }
        }

        // Parse contacts JSON if present
        let contacts = [];
        if (record.Contacts) {
          try {
            contacts = JSON.parse(record.Contacts);
          } catch (e) {
            console.warn('Failed to parse contacts JSON:', e);
          }
        }

        // Parse sales reps (comma-separated)
        let salesReps = [];
        if (record['Sales Reps']) {
          salesReps = record['Sales Reps'].split(',').map(name => ({
            salesRepName: name.trim()
          }));
        }

        return {
          apparelMagicId: record['Customer ID'] || null,
          customerName: record['Customer Name'] || '',
          customerEmail: record['Email'] || '',
          customerPhone: record['Phone'] || '',
          customerStatus: record['Status'] || 'Lead',
          customerAddress1: record['Address 1'] || '',
          customerAddress2: record['Address 2'] || '',
          customerCity: record['City'] || '',
          customerState: record['State'] || '',
          customerZip: record['Zip'] || '',
          customerCountry: record['Country'] || 'US',
          website: record['Website'] || '',
          businessType: record['Business Type'] || '',
          businessFocus: record['Business Focus'] || '',
          salesReps: salesReps,
          locations: locations,
          contacts: contacts,
          lastModified: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      });

      // Validate records
      const validateRecord = (record) => {
        if (!record.customerName || record.customerName.trim() === '') {
          return 'Customer Name is required';
        }
        return null;
      };

      // Fetch current customers for comparison
      const currentSource = filteredMode ? filterBase : customers;

      // Import with sync (destructive)
      const results = await importAndSyncCollectionFromCSV(
        processedData,
        db,
        collectionPath('customers'),
        (record) => record.customerName, // Unique key is customer name
        currentSource,
        null, // No progress callback
        validateRecord,
        (record) => can(FEATURE_PERMISSIONS.CUSTOMER_DELETE), // Can delete if has delete permissions
        null // No number generator needed
      );

      // Show results
      const message = 
        `Import Complete!\n\n` +
        `✅ Added: ${results.added}\n` +
        `📝 Updated: ${results.updated}\n` +
        `🗑️ Deleted: ${results.deleted}\n` +
        `${results.errors.length > 0 ? `\n⚠️ Errors: ${results.errors.length}` : ''}`;

      setToast({
        message: message,
        type: results.errors.length > 0 ? 'error' : 'success'
      });

      if (results.errors.length > 0) {
        console.error('Import errors:', results.errors);
      }

      // Refresh the list
      if (filteredMode) {
        await loadFilteredBase();
      } else {
        await fetchCustomers();
      }

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      console.error('Import failed:', err);
      setToast({
        message: `Import failed: ${err.message}`,
        type: 'error'
      });
    } finally {
      setImporting(false);
    }
  };

  // Handle match to existing customer (opens dialog)
  const handleMatchToExisting = (customer) => {
    setCustomerToMatch(customer);
    setShowMatchDialog(true);
  };

  // Handle merge confirmation from dialog
  const handleConfirmMerge = async (sourceCustomer, targetCustomer) => {
    setMerging(true);
    try {
      const results = await mergeCustomers(sourceCustomer, targetCustomer, currentUser);
      
      setToast({
        message: `Successfully merged "${sourceCustomer.customerName}" into "${targetCustomer.customerName}". ` +
                 `Updated ${results.opportunitiesUpdated} opportunities, ${results.ordersUpdated} orders, ` +
                 `${results.contactsUpdated} contacts, and ${results.tasksUpdated} tasks.`,
        type: 'success'
      });
      
      // Close dialog
      setShowMatchDialog(false);
      setCustomerToMatch(null);
      
      // Refresh data
      if (filteredMode) {
        await loadFilteredBase();
      } else {
        await fetchCustomers();
      }
    } catch (err) {
      console.error('Merge failed:', err);
      setToast({
        message: `Merge failed: ${err.message}`,
        type: 'error'
      });
    } finally {
      setMerging(false);
    }
  };

  // Handle mark as reviewed (without merge)
  const handleMarkReviewed = async (customer) => {
    const confirmed = window.confirm(
      `Mark "${customer.customerName}" as reviewed?\n\n` +
      `This will remove it from the review list without merging.`
    );
    
    if (!confirmed) return;
    
    try {
      await markCustomerReviewed(customer.id, currentUser);
      
      setToast({
        message: `"${customer.customerName}" marked as reviewed`,
        type: 'success'
      });
      
      // Refresh data
      if (filteredMode) {
        await loadFilteredBase();
      } else {
        await fetchCustomers();
      }
    } catch (err) {
      console.error('Mark reviewed failed:', err);
      setToast({
        message: `Failed to mark as reviewed: ${err.message}`,
        type: 'error'
      });
    }
  };

  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      {/* Customer Match Dialog */}
      {showMatchDialog && customerToMatch && (
        <CustomerMatchDialog
          sourceCustomer={customerToMatch}
          allCustomers={customers}
          onClose={() => {
            setShowMatchDialog(false);
            setCustomerToMatch(null);
          }}
          onConfirmMerge={handleConfirmMerge}
        />
      )}
      
      {(showAddModal || editCustomer) && (
        <CustomerModal
          user={currentUser}
          users={users}
          initial={editCustomer}
          onClose={(changed) => {
            setShowAddModal(false);
            setEditCustomer(null);
            if (changed) {
              // Re-evaluate current mode
              if (filteredMode) loadFilteredBase();
              else fetchCustomers();
            }
          }}
          onAddCustomer={async (customerData) => {
            await addDoc(
              collection(db, collectionPath('customers')),
              {
                ...customerData,
                uid: user.uid,
                dateCreated: new Date().toISOString()
              }
            );
            // FIX: Always refresh the filtered base if in filtered mode
            if (filteredMode) {
              await loadFilteredBase();
            } else {
              await fetchCustomers();
            }
          }}
        />
      )}

      {/* Review Mode Banner */}
      {(() => {
        const shouldShow = !showReviewMode && !dismissedReviewBanner && reviewCount > 0 && can(FEATURE_PERMISSIONS.CUSTOMER_MERGE);
        return shouldShow;
      })() && (
        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-semibold text-yellow-800">
                  {reviewCount} {reviewCount === 1 ? 'customer' : 'customers'} need review
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  These customers were auto-created from imports and may need to be merged with existing customers.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowReviewMode(true)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium transition-colors"
              >
                Review Now
              </button>
              <button
                onClick={() => {
                  // Dismiss banner for this session (will show again on next login/page reload)
                  setDismissedReviewBanner(true);
                  setToast({ 
                    message: 'Banner dismissed for this session. It will show again on your next login.', 
                    type: 'info' 
                  });
                }}
                className="px-3 py-2 text-yellow-700 hover:text-yellow-900"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Mode Active Banner */}
      {showReviewMode && (
        <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-lg shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🔍</span>
              <div>
                <p className="font-semibold text-blue-800">
                  Review Mode Active
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  Showing {reviewCount} {reviewCount === 1 ? 'customer' : 'customers'} that need review
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowReviewMode(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              Exit Review Mode
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Customers</h2>
        {can(FEATURE_PERMISSIONS.CUSTOMER_CREATE) && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500"
          >
            Add New Customer
          </button>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-4 space-y-2 md:space-y-0">
        <div className="flex items-center w-full md:w-auto">
          <span className="mr-2 font-medium text-gray-700 whitespace-nowrap">Search:</span>
          <input
            type="text"
            placeholder="Search by name, email, ID, city, or state..."
            className={`border rounded px-3 py-2 w-full md:w-64 transition-colors duration-150 ${fb.draft.search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
            value={fb.draft.search}
            onChange={e => fb.setDraft('search', e.target.value)}
            onKeyDown={fb.applyOnEnter}
          />
        </div>
        
        <div className="flex items-center w-full md:w-auto">
          <span className="mr-2 font-medium text-gray-700 whitespace-nowrap">Status:</span>
          <select
            className={`border rounded px-3 py-2 w-full md:w-48 transition-colors duration-150 ${fb.draft.stageFilter ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
            value={fb.draft.stageFilter}
            onChange={e => fb.setDraft('stageFilter', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Lead">Lead</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        
        {can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL) && (
          <div className="flex items-center w-full md:w-auto">
            <span className="mr-2 font-medium text-gray-700 whitespace-nowrap">Sales Rep:</span>
            <select
              className={`border rounded px-3 py-2 w-full md:w-48 transition-colors duration-150 ${fb.draft.salesRepFilter ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
              value={fb.draft.salesRepFilter}
              onChange={e => fb.setDraft('salesRepFilter', e.target.value)}
            >
              <option value="">All Sales Reps</option>
              <option value="(No Sales Rep)">(No Sales Rep)</option>
              {availableSalesReps.map(repName => (
                <option key={repName} value={repName}>
                  {repName}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <div className="flex items-center w-full md:w-auto">
          <span className="mr-2 font-medium text-gray-700 whitespace-nowrap">Focus:</span>
          <select
            className={`border rounded px-3 py-2 w-full md:w-40 transition-colors duration-150 ${fb.draft.businessFocusFilter ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
            value={fb.draft.businessFocusFilter}
            onChange={e => fb.setDraft('businessFocusFilter', e.target.value)}
          >
            <option value="">All Focus</option>
            <option value="__EMPTY__">(Unassigned)</option>
            {focusOptions.map(focus => (
              <option key={focus} value={focus}>
                {focus}
              </option>
            ))}
          </select>
        </div>
        
        <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      <div className="flex items-center mb-4 space-x-2">
        {can(FEATURE_PERMISSIONS.CUSTOMER_EXPORT) && (
        <ExportButton
          onExport={handleExport}
          formats={['csv', 'xlsx', 'pdf']}
          disabled={loading}
          tooltipText={selectedCustomers.size > 0 
            ? `Export ${selectedCustomers.size} selected customers` 
            : 'Export all customers in various formats'}
          label={selectedCustomers.size > 0 
            ? `Export Selected (${selectedCustomers.size})` 
            : 'Export All Customers'
          }
          className="bg-green-600 hover:bg-green-700"
        />
        )}

        {/* Destructive Import Button */}
        {can(FEATURE_PERMISSIONS.CUSTOMER_IMPORT) && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleDestructiveImport}
              style={{ display: 'none' }}
            />
            <Tooltip text={
              <div className="text-left">
                <div className="font-semibold text-red-300 mb-1">⚠️ DESTRUCTIVE IMPORT</div>
                <div className="text-sm">Admin only: Replace entire customer list with CSV data.</div>
                <div className="text-sm text-red-200 mt-1">This DELETES customers not in the file!</div>
              </div>
            } maxWidth={300}>
              <button
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                {importing ? 'Importing...' : 'Destructive Import'}
              </button>
            </Tooltip>
          </>
        )}
        
        {selectedCustomers.size > 0 && (
          <Tooltip text="Deselect all customers">
            <button
              className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-400 text-sm"
              onClick={() => {
                setSelectedCustomers(new Set());
                setSelectAll(false);
              }}
            >
              Clear Selection
            </button>
          </Tooltip>
        )}
        
        {/* Bulk Delete Button - Admin/Manager Only */}
        {selectedCustomers.size > 0 && can(FEATURE_PERMISSIONS.CUSTOMER_DELETE) && (
          <Tooltip text="Permanently delete all selected customers">
            <button
              className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center gap-2"
              onClick={handleBulkDelete}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Delete Selected ({selectedCustomers.size})
            </button>
          </Tooltip>
        )}
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full whitespace-no-wrap">
          <thead>
            <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded"
                  disabled={loading || visibleCustomers.length === 0}
                  title="Select all customers on this page"
                />
              </th>
              <th 
                className="px-4 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('customerId')}
                title="Unique identifier from ApparelMagic system. Click to sort."
              >
                <div className="flex items-center">
                  Customer ID
                  <SortIcon field="customerId" />
                </div>
              </th>
              <th 
                className="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('customerName')}
                title="the customer's business name. Click to sort."
              >
                <div className="flex items-center">
                  Customer Name
                  <SortIcon field="customerName" />
                </div>
              </th>
              <th className="px-4 py-3" title="Primary email address for the customer">
                Email
              </th>
              <th className="px-4 py-3" title="Primary phone number for the customer">
                Phone
              </th>
              {showReviewMode && (
                <>
                  <th className="px-4 py-3" title="Contact name from import (for matching)">
                    Contact Name
                  </th>
                  <th className="px-4 py-3" title="Contact email from import (for matching)">
                    Contact Email
                  </th>
                  <th className="px-4 py-3" title="Contact phone from import (for matching)">
                    Contact Phone
                  </th>
                </>
              )}
              <th className="px-4 py-3" title="City where the customer is located">
                City
              </th>
              <th className="px-4 py-3" title="State where the customer is located">
                State
              </th>
              <th 
                className="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('salesRep')}
                title="Sales representatives assigned to this customer. Click to sort."
              >
                <div className="flex items-center">
                  Sales Rep
                  <SortIcon field="salesRep" />
                </div>
              </th>
              <th className="px-4 py-3" title="Business focus areas (Golf, Tennis, etc.)">
                Focus
              </th>
              <th 
                className="px-4 py-3 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => handleSort('status')}
                title="Current status of the customer (Active, Lead, Inactive, etc.). Click to sort."
              >
                <div className="flex items-center">
                  Status
                  <SortIcon field="status" />
                </div>
              </th>
              <th className="px-4 py-3" title="Available actions for this customer">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y">
            {loading && (
              <tr>
                <td colSpan={showReviewMode ? "14" : "11"} className="text-center py-4">Loading customers...</td>
              </tr>
            )}
            {!loading && visibleCustomers.length === 0 && (
              <tr>
                <td colSpan={showReviewMode ? "14" : "11"} className="text-center py-4">
                  {error || 'No customers found.'}
                </td>
              </tr>
            )}
            {!loading && visibleCustomers.map(customer => (
              <tr key={customer.id} className={`text-gray-700 hover:bg-gray-50 ${
                selectedCustomers.has(customer.id) ? 'bg-blue-50' : ''
              }`}>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedCustomers.has(customer.id)}
                    onChange={(e) => handleSelectCustomer(customer.id, e.target.checked)}
                    className="rounded"
                  />
                </td>
                <td className="px-4 py-3 font-mono">
                  {customer.customerAccountNumber ? customer.customerAccountNumber : <span className="text-gray-400">(none)</span>}
                </td>
                <td
                  className="px-4 py-3 font-semibold text-indigo-600 hover:underline cursor-pointer"
                  onClick={() => navigate(`/customer/${customer.id}`)}
                  style={{ maxWidth: '250px' }}
                  title={customer.customerName}
                >
                  <div className="truncate">
                    {customer.customerName}
                  </div>
                </td>
                <td className="px-4 py-3 truncate max-w-[180px]" style={{ maxWidth: 180 }} title={customer.customerEmail}>
                  {customer.customerEmail}
                </td>
                <td className="px-4 py-3">{customer.customerPhone}</td>
                {showReviewMode && (
                  <>
                    <td className="px-4 py-3" style={{ maxWidth: '150px' }} title={customer.importedContactName}>
                      <div className="truncate text-sm text-gray-600">
                        {customer.importedContactName || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 truncate max-w-[180px]" style={{ maxWidth: 180 }} title={customer.importedContactEmail}>
                      <div className="text-sm text-gray-600">
                        {customer.importedContactEmail || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-600">
                        {customer.importedContactPhone || '-'}
                      </div>
                    </td>
                  </>
                )}
                <td className="px-4 py-3">{customer.customerCity}</td>
                <td className="px-4 py-3">{customer.customerState}</td>
                <td className="px-4 py-3" style={{ maxWidth: '150px' }} title={getSalesRepDisplay(customer)}>
                  <div className="truncate">
                    {getSalesRepDisplay(customer)}
                  </div>
                </td>
                <td className="px-4 py-3" style={{ maxWidth: '120px' }} title={Array.isArray(customer.businessFocus) ? customer.businessFocus.join(', ') : customer.businessFocus || ''}>
                  <div className="truncate text-sm text-gray-600">
                    {Array.isArray(customer.businessFocus) ? customer.businessFocus.join(', ') : customer.businessFocus || '-'}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    customer.customerStatus === 'Approved' ? 'bg-green-100 text-green-700' :
                    customer.customerStatus === 'Cancelled' || customer.customerStatus === 'Inactive' ? 'bg-gray-100 text-red-700' :
                    customer.customerStatus === 'Pending' || customer.customerStatus === 'Lead' ? 'bg-yellow-100 text-yellow-700' : ''
                  }`}>
                    {customer.customerStatus || '-'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {showReviewMode ? (
                      /* Review Mode Actions */
                      <>
                        <button 
                          onClick={() => handleMatchToExisting(customer)} 
                          className="text-blue-600 hover:underline px-2 py-1 font-medium"
                        >
                          Match to Existing
                        </button>
                        <button 
                          onClick={() => handleMarkReviewed(customer)} 
                          className="text-green-600 hover:underline px-2 py-1"
                        >
                          Mark Reviewed
                        </button>
                        {can(FEATURE_PERMISSIONS.CUSTOMER_DELETE) && (
                          <button 
                            onClick={() => handleDeleteCustomer(customer)} 
                            className="text-red-600 hover:underline px-2 py-1"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    ) : (
                      /* Normal Mode Actions */
                      <>
                        {can(FEATURE_PERMISSIONS.CUSTOMER_EDIT) && (
                          <button onClick={() => setEditCustomer(customer)} className="text-indigo-600 hover:underline px-2 py-1">
                            Edit
                          </button>
                        )}
                        {can(FEATURE_PERMISSIONS.CUSTOMER_DELETE) && (
                          <button 
                            onClick={() => handleDeleteCustomer(customer)} 
                            className="text-red-600 hover:underline px-2 py-1"
                          >
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls - Show when searching or filtering */}
      {usePagination && (
        <PaginationControls currentPage={currentPage} totalPages={calculatedTotalPages} onPageChange={handlePageChange} totalItems={allCustomers.length} pageSize={PAGE_SIZE} itemLabel={isSearchMode ? 'results' : 'customers'} />
      )}
      
      {/* Browse Mode Summary - Show total loaded customers */}
      {!usePagination && allCustomers.length > 0 && (
        <div className="flex justify-center py-4">
          <span className="text-sm text-gray-500">
            Showing {allCustomers.length} customers {hasMoreCustomers && '(click "Load More" or use the Search Filters above)'}
          </span>
        </div>
      )}

      {/* Load More Button (Hybrid Lazy Loading) */}
      {!searchLower && !fb.applied.stageFilter && !fb.applied.salesRepFilter && !showReviewMode && hasMoreCustomers && (
        <div className="flex justify-center py-4 border-t border-gray-200">
          <button
            onClick={loadMoreCustomers}
            disabled={loadingMore}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Load More Customers
              </>
            )}
          </button>
        </div>
      )}
    </main>
  );
};

export default CustomerPage;
